/**
 * Servico de auditoria — leitura do trilho que o repositorio escreve.
 *
 * Isto e o que responde a pergunta que mais vezes se ouve numa familia a gerenciar
 * dinheiro e saude em conjunto: "quem alterou isto?". Sem uma resposta rapida e
 * legivel, a partilha de dados gera desconfianca e o aplicativo deixa de ser usada.
 *
 * Aqui so se le. Escrever auditoria fora do repositorio partiria a garantia de
 * que cada entrada corresponde a uma escrita real.
 */
import type { AuditActor, AuditOperation, BaseRecord } from '@flos/shared';
import type { AuditRow, FlosDatabase } from './db';
import type { SessionService } from './session';

export interface AuditQuery {
  limit?: number;
  /** Mais recente primeiro (por padrão) ou mais antigo primeiro. */
  order?: 'desc' | 'asc';
  operation?: AuditOperation;
  actor?: AuditActor;
}

/** Um ponto no tempo de vida de um registro. */
export interface AuditVersion {
  /** Versao do registro depois desta operacao. */
  version: number;
  at: string;
  operation: AuditOperation;
  actor: AuditActor;
  memberId: string;
  /** Estado completo do registro depois da operacao. */
  snapshot: unknown;
  /** Campos que mudaram face a versao anterior. Guia a interface do historico. */
  changedFields: string[];
  entryId: string;
  revertedFrom?: string;
}

export class AuditService {
  readonly #db: FlosDatabase;
  readonly #session: SessionService;

  constructor(db: FlosDatabase, session: SessionService) {
    this.#db = db;
    this.#session = session;
  }

  async get(id: string): Promise<AuditRow | null> {
    const row = await this.#db.audit.get(id);
    if (!row || row.familyId !== this.#session.familyId) return null;
    return row;
  }

  /** Todas as operacoes sobre um registro concreto. */
  async byRecord(entity: string, recordId: string, query: AuditQuery = {}): Promise<AuditRow[]> {
    const rows = await this.#db.audit
      .where('[entity+recordId]')
      .equals([entity, recordId])
      .toArray();
    return this.#finish(rows, query);
  }

  /** Todas as operacoes sobre uma entidade, ex. 'health.medication'. */
  async byEntity(entity: string, query: AuditQuery = {}): Promise<AuditRow[]> {
    const rows = await this.#db.audit.where('entity').equals(entity).toArray();
    return this.#finish(rows, query);
  }

  /** O que uma pessoa fez. Usado na tela de atividade da familia. */
  async byMember(memberId: string, query: AuditQuery = {}): Promise<AuditRow[]> {
    const rows = await this.#db.audit.where('memberId').equals(memberId).toArray();
    return this.#finish(rows, query);
  }

  /** Intervalo de tempo, limites ISO-8601 inclusivos. */
  async byTimeRange(from: string, to: string, query: AuditQuery = {}): Promise<AuditRow[]> {
    const familyId = this.#session.familyId;
    const rows = await this.#db.audit
      .where('[familyId+at]')
      .between([familyId, from], [familyId, to], true, true)
      .toArray();
    return this.#finish(rows, query);
  }

  /** Atividade recente da familia, para o cartao "o que mudou". */
  async recent(limit = 20, query: AuditQuery = {}): Promise<AuditRow[]> {
    const familyId = this.#session.familyId;
    const rows = await this.#db.audit.where('familyId').equals(familyId).toArray();
    return this.#finish(rows, { ...query, limit });
  }

  /**
   * Linha do tempo completa de um registro, da criacao ate hoje, com os campos
   * alterados em cada passo. E daqui que sai a tela "historico de alteracoes".
   */
  async history(entity: string, recordId: string): Promise<AuditVersion[]> {
    const rows = await this.byRecord(entity, recordId, { order: 'asc' });
    const out: AuditVersion[] = [];
    for (const row of rows) {
      const after = row.after as (BaseRecord & Record<string, unknown>) | null;
      const before = row.before as Record<string, unknown> | null;
      const version: AuditVersion = {
        version: after?.version ?? out.length + 1,
        at: row.at,
        operation: row.operation,
        actor: row.actor,
        memberId: row.memberId,
        snapshot: row.after,
        changedFields: diffFields(before, after as Record<string, unknown> | null),
        entryId: row.id,
      };
      if (row.revertedFrom) version.revertedFrom = row.revertedFrom;
      out.push(version);
    }
    return out;
  }

  /** Filtra pela familia ativa, ordena e limita. */
  #finish(rows: AuditRow[], query: AuditQuery): AuditRow[] {
    const familyId = this.#session.familyId;
    let out = rows.filter((row) => row.familyId === familyId);
    if (query.operation) out = out.filter((row) => row.operation === query.operation);
    if (query.actor) out = out.filter((row) => row.actor === query.actor);
    const factor = query.order === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => factor * compareEntries(a, b));
    if (typeof query.limit === 'number' && query.limit >= 0) out = out.slice(0, query.limit);
    return out;
  }
}

/**
 * Ordem cronologica fiavel. O carimbo `at` tem precisao de milissegundo e duas
 * escritas seguidas caem quase sempre no mesmo milissegundo, por isso desempata-se
 * pela versao do registro — que e estritamente crescente — e so depois pelo id.
 */
function compareEntries(a: AuditRow, b: AuditRow): number {
  if (a.at !== b.at) return a.at.localeCompare(b.at);
  const versionA = versionOf(a);
  const versionB = versionOf(b);
  if (versionA !== versionB) return versionA - versionB;
  return a.id.localeCompare(b.id);
}

function versionOf(row: AuditRow): number {
  const after = row.after as { version?: unknown } | null;
  return typeof after?.version === 'number' ? after.version : 0;
}

/** Campos com valor diferente entre duas versoes do registro. */
function diffFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed: string[] = [];
  for (const key of keys) {
    // `updatedAt`/`version` mudam sempre; mostrá-los seria ruido no historico.
    if (key === 'updatedAt' || key === 'updatedBy' || key === 'version' || key === 'syncState') {
      continue;
    }
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) changed.push(key);
  }
  return changed.sort();
}
